"use client";

import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import obituaryService from "@/services/obituary-service";
import { matchesObituaryName } from "@/utils/obituaryNameSearch";
import TextFieldComp from "@/app/components/appcomponents/TextFieldComp";
import DescriptionFieldComp from "@/app/components/appcomponents/DescriptionFieldComp";
import ButtonBlueBorder from "@/app/components/appcomponents/ButtonBlueBorder";
import { PopupShell, AlignIcon, ChoiceOption } from "./parts";

// Limits from the client's fixes document.
export const MAX_HEADLINE_LENGTH = 16;
export const MAX_AUTHOR_LENGTH = 16;
export const MAX_MESSAGE_LENGTH = 5000;
export const MIN_MESSAGE_LENGTH = 10;

// The two looks of the box; the same colours the swatches show in the design.
const CREAM_SWATCH = "#FCF0DD";
const NAVY_SWATCH = "#1E4882";

// What is still missing on the form, by field; empty when step 1 is complete.
export function stepMessageErrors(form) {
  const errors = {};
  if (!form.obituary) errors.obituary = "Izberi pokojno osebo s seznama";
  if (!form.headline.trim()) errors.headline = "Napiši naslov";
  if (form.message.trim().length < MIN_MESSAGE_LENGTH) {
    errors.message = `Besedilo je prekratko (vsaj ${MIN_MESSAGE_LENGTH} znakov)`;
  }
  if (!form.authorName.trim()) errors.authorName = "Napiši svoje ime";
  return errors;
}

export function isStepMessageValid(form) {
  return Object.keys(stepMessageErrors(form)).length === 0;
}

// Lower case, no diacritics, single spaces: "Franc  Kovačič" equals "franc kovacic".
const normalizeName = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

function FieldError({ children }) {
  return children ?
      <p role="alert" className="mt-1 text-[13px] leading-[16px] text-[#C62828]">
        {children}
      </p>
    : null;
}

/** Step 1: who the message is about, headline, text, how it looks and the signature. */
export default function StepMessage({ form, update, onNext, onClose, obituaryLocked = false }) {
  const [suggestions, setSuggestions] = useState([]);
  // "idle" | "done" (the last search returned) | "error" (the search itself failed)
  const [searchState, setSearchState] = useState("idle");
  const [showErrors, setShowErrors] = useState(false);
  const searchSeq = useRef(0);
  const { obituary, search } = form;
  const errors = showErrors ? stepMessageErrors(form) : {};

  // Tells the author what to do about the person, depending on what the search showed.
  const personMessage = () => {
    if (suggestions.length > 0) return "Klikni ime pokojne osebe v seznamu pod poljem";
    if (searchState === "error") return "Iskanje trenutno ni na voljo";
    if (search.trim().length >= 2 && searchState === "done") {
      return "Osebe ne najdem. Preveri črkovanje imena";
    }
    return "Vpiši ime pokojne osebe in ga izberi iz seznama";
  };

  const handleNext = () => {
  const missing = stepMessageErrors(form);

  if (Object.keys(missing).length > 0) {
    setShowErrors(true);
    toast.error(missing.obituary ? personMessage() : Object.values(missing)[0]);
    return;
  }

  onNext(form);
};

  // Find the person the message is about, as the user types.
  useEffect(() => {
    const term = search.trim();
    if (obituary || term.length < 2) {
      setSuggestions([]);
      setSearchState("idle");
      return;
    }
    const seq = ++searchSeq.current;
    const timer = setTimeout(async () => {
      try {
        const response = await obituaryService.getObituary({
          name: term,
          limit: 30,
          page: 1,
        });

        if (seq === searchSeq.current) {
          // Same matching as the name search on the Funerals page.
          const found = response?.error ? [] : (response.obituaries ?? []);
          setSuggestions(found.filter((item) => matchesObituaryName(item, term)).slice(0, 8));
          setSearchState(response?.error ? "error" : "done");
        }
      } catch {
        if (seq === searchSeq.current) {
          setSuggestions([]);
          setSearchState("error");
        }
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, obituary]);

  const title = "Dodaj zahvalo, spomine, posvetilo";

  return (
    <PopupShell
      title="Dodaj zahvalo, spomin"
      titleClassName="hidden mobile:block"
      panelTitle={title}
      figmaDesktop
      panelTopClass="[@media(min-width:744px)]:!mt-[28px]"
      panelPadding="px-8 pb-[38px] pt-[40px]"
      onClose={onClose}
    >
      <h2 className="font-variation-customOpt24 text-[24px] font-medium leading-[28px] text-[#1E2125] mobile:hidden">
        {title}
      </h2>

      {obituaryLocked && obituary ?
        <div className="mt-[24px] mobile:mt-0">
          <TextFieldComp
            readOnly
            background="#E8EBF0"
            value={`Spominska stran: ${obituary.name} ${obituary.sirName}${obituary.city ? `, ${obituary.city}` : ""}`}
          />
        </div>
      : <div className="relative mt-[24px] mobile:mt-0">
        <TextFieldComp
          placeholder="Ime ali priimek pokojne/ga"
          background="#F3F2E6"
          value={obituary ? `${obituary.name} ${obituary.sirName}` : search}
          onChange={(e) => update({ obituary: null, search: e.target.value })}
        />
        {suggestions.length > 0 && (
          <ul className="absolute z-10 mt-1 max-h-[240px] w-full overflow-y-auto rounded-[6px] bg-[#f1fffe] py-1 shadow-lg">
            <li className="px-3 pb-1 pt-1 text-[12px] text-[#6D778E]">Klikni ime pokojne osebe:</li>
            {suggestions.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    update({ obituary: item });
                    setSuggestions([]);
                  }}
                  className="w-full px-3 py-2 text-left text-[#7d7d7d] hover:bg-[#6D778E] hover:text-white"
                >
                  {item.name} {item.sirName}
                  {item.location ? ` – ${item.location}` : ""}
                </button>
              </li>
            ))}
          </ul>
        )}
        {!obituary && (showErrors || search.trim().length >= 2) && (
          <p
            role={showErrors ? "alert" : undefined}
            className={`mt-1 text-[13px] leading-[16px] ${showErrors ? "text-[#C62828]" : "text-[#6D778E]"}`}
          >
            {personMessage()}
          </p>
        )}
      </div>}

      <div className="mt-[20px] flex mobile:hidden">
        <TextFieldComp
          placeholder="Napiši naslov, npr. Posvetilo, Zadnji pozdrav, Zahvala, Spomin,..."
          heightClass="h-[50px]"
          value={form.headline}
          onChange={(e) => update({ headline: e.target.value })}
          maxLength={MAX_HEADLINE_LENGTH}
        />
      </div>
      <div className="mt-[20px] hidden mobile:flex">
        <TextFieldComp
          placeholder="Napiši naslov"
          value={form.headline}
          onChange={(e) => update({ headline: e.target.value })}
          maxLength={MAX_HEADLINE_LENGTH}
        />
      </div>
      <FieldError>{errors.headline}</FieldError>
      <div className="mt-2 hidden text-[13px] leading-[18px] text-[#6D778E] mobile:block">
        npr.Posvetilo, Zadnji pozdrav, Zahvala, Spomin
      </div>

      <div className="relative mt-[24px] h-[170px] mobile:mt-[22px]">
        <DescriptionFieldComp
          placeholder="Dodaj tekst. Omejitev je 5.000 znakov."
          height="170px"
          value={form.message}
          onChange={(e) => update({ message: e.target.value })}
          maxLength={MAX_MESSAGE_LENGTH}
        />
        {!form.message && (
          <p className="pointer-events-none absolute bottom-3 right-4 text-right text-[14px] leading-[20px] text-[#7C879A] mobile:left-4 mobile:text-center mobile:text-[13px]">
            <span className="mobile:hidden">
              Brez skrbi. Pred objavo boš lahko preveril/a, kako izgleda dopis
            </span>
            <span className="hidden mobile:inline">
              Brez skrbi. Pred objavo boš lahko preveril/a, kako sporočilo izgleda.
            </span>
          </p>
        )}
      </div>

      <FieldError>{errors.message}</FieldError>

      <p className="mt-[34px] text-[15px] text-[#6D778E] mobile:mt-[24px]">
        Izberi prikaz: poravnavo in barvo podlage
      </p>
      <div className="mt-3 flex items-center justify-between mobile:justify-start mobile:gap-[56px]">
        <div role="radiogroup" aria-label="Poravnava besedila" className="flex items-center gap-[14px] mobile:gap-[16px]">
          <ChoiceOption
            label="Levo poravnano"
            selected={form.alignment === "left"}
            onSelect={() => update({ alignment: "left" })}
          >
            <AlignIcon align="left" />
          </ChoiceOption>
          <span className="text-[18px] text-[#414B5A] mobile:hidden">ALI</span>
          <ChoiceOption
            label="Sredinsko poravnano"
            selected={form.alignment === "center"}
            onSelect={() => update({ alignment: "center" })}
          >
            <AlignIcon align="center" />
          </ChoiceOption>
        </div>
        <div role="radiogroup" aria-label="Barva podlage" className="flex items-center gap-[14px] mobile:gap-[16px]">
          <ChoiceOption
            label="Svetla podlaga"
            swatch={CREAM_SWATCH}
            selected={form.background === "cream"}
            onSelect={() => update({ background: "cream" })}
          />
          <span className="text-[18px] text-[#414B5A] mobile:hidden">ALI</span>
          <ChoiceOption
            label="Temna podlaga"
            swatch={NAVY_SWATCH}
            selected={form.background === "navy"}
            onSelect={() => update({ background: "navy" })}
          />
        </div>
      </div>

      <div className="mt-[56px] flex mobile:mt-[28px]">
        <TextFieldComp
          placeholder="Napiši svoje ime, ki naj bo prikazano"
          heightClass="h-[50px] mobile:h-[40px]"
          value={form.authorName}
          onChange={(e) => update({ authorName: e.target.value })}
          maxLength={MAX_AUTHOR_LENGTH}
        />
      </div>

      <FieldError>{errors.authorName}</FieldError>

      <div className="mt-[20px] w-[250px] mobile:w-full">
        <ButtonBlueBorder placeholder="Naprej" onClick={handleNext} />
      </div>
    </PopupShell>
  );
}
